import { clientLogger } from '@/lib/clientLogger';
import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { useSectionOrder } from '@/hooks/useSectionOrder';
import { ReorderableSections, ReorderButton } from './ui/ReorderableSections';
import { ItemEditorDialog } from './ui/ItemEditorDialog';
import { STATUS_BADGE_CLASS, statusBadgeStyle, statusDotColor } from '@/lib/statusChip';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService, apiErrorMessage } from '@/lib/api';
import { CreateJobDTO, JobTypeBasicDTO, PaymentStatus, CompanyBasicDTO, CompanyDTO, JobSourceDTO, ExtraFieldConfigDTO, JobStatusDTO, ContactCreateDTO, ContactDTO, ContactUpdateDTO, JobContactInputDTO, TagDTO, JobTextAnalysisDTO } from '@/types';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { X, Calendar, MapPin, User, FileText, DollarSign, Plus, Settings, ArrowRight, ChevronDown, ChevronUp, Sparkles, ListPlus } from 'lucide-react';
import { CustomSelect } from './ui/CustomSelect';
import { gentleDateTimePickerRootSx, gentleDateTimePickerSlotProps, usePickerValue } from './ui/gentleDateTimePicker';
import { toDateTimeInputValue, isScheduleRange, parseBackendInstantToUtcDate, toDateTimeInputValueInTz, dateTimeInputInTimeZoneToIso } from '@/lib/dateUtils';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs from 'dayjs';

import { useExtensibleFields } from '@/hooks/useExtensibleFields';
import { useGoogleMapsAutocomplete, getAddressWithZipCode } from '@/hooks/useGoogleMapsAutocomplete';
import { MapWidget } from './MapWidget';
import { SchedulerAssistant } from './SchedulerAssistant';
import { ContactListItem } from './contacts/ContactList';
import { ContactFormModal } from './contacts/ContactFormModal';
import { AddCustomFieldModal } from './AddCustomFieldModal';
import { ContactSearchModal } from './contacts/ContactSearchModal';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useVisualKeyboardOpen } from '@/hooks/useVisualKeyboardOpen';
import { useGlobalCompany } from '@/hooks/useGlobalCompany';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { TagSelector } from './ui/TagSelector';
import { buildTechnicianSelectOptions, hasInactiveTechnicians } from '@/lib/technicianSelectors';

import { createPortal } from 'react-dom';
import { useBackNavigationLayer } from '@/lib/navigation';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { useSplitScreenBounds } from '@/lib/splitScreen';
import { usePermissions } from '@/hooks/usePermissions';
import { CallMaskingOptions } from './CallMaskingOptions';

interface NewJobModalProps {
    isOpen: boolean;
    onClose: () => void;
    onJobCreated: () => void;
    initialValues?: NewJobInitialValues;
}

export interface NewJobInitialValues {
    address?: string;
    unit_name?: string;
    company_id?: string;
    assigned_technician_id?: string;
    notes?: string;
    job_type_id?: string;
    origin_job_id?: string;
    contacts?: ContactDTO[];
}

interface PendingContact {
    id: string;
    contact: ContactDTO;
    createPayload?: ContactCreateDTO;
    notes?: string;
}

/**
 * Scroll region + optional pinned footer. Structure is always the same so toggling
 * keyboard visibility does not swap wrapper nodes (which would remount the form and dismiss the keyboard).
 */
function NewJobModalScrollLayout({
    mobileStickyFooter,
    children,
}: {
    mobileStickyFooter: ReactNode;
    children: ReactNode;
}) {
    return (
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
            {mobileStickyFooter}
        </div>
    );
}

const DEFAULT_NEW_JOB_SECTION_ORDER = ['location', 'contacts', 'date', 'jobTypeSource', 'description', 'tags', 'estimateTechnician', 'notes', 'internalNotes', 'additionalFields'] as const;
type NewJobSectionId = typeof DEFAULT_NEW_JOB_SECTION_ORDER[number];
const NEW_JOB_SECTION_LABELS: Record<NewJobSectionId, string> = {
        location: 'Location',
        contacts: 'Contacts',
        date: 'Date',
        jobTypeSource: 'Job Type & Source',
        description: 'Description',
        tags: 'Tags',
        estimateTechnician: 'Estimate & Technician',
        notes: 'Notes',
        internalNotes: 'Internal Notes',
        additionalFields: 'Additional Fields',
};

export function NewJobDesktop({ isOpen, onClose, onJobCreated, initialValues }: NewJobModalProps) {
    const isMobile = useIsMobile();
    const isVirtualKeyboardOpen = useVisualKeyboardOpen(isOpen && isMobile);
    // Split screen open: the form covers only the side it was opened from.
    const splitBounds = useSplitScreenBounds();
    // A narrow split side can't fit the form next to the 400px map column, so the two share it evenly.
    const isNarrowSplit = !!splitBounds && window.innerWidth - Number(splitBounds.left) - Number(splitBounds.right) < 1000;
    const { selectedCompanyId: globalCompanyId } = useGlobalCompany();
    const { userRole, isAdmin } = useCurrentUser();
    const { isFeatureAllowed } = usePermissions();
    // Each user can arrange the form's boxes ("Reorder" in the header). Saved on this device.
    const sectionOrder = useSectionOrder('newJob', DEFAULT_NEW_JOB_SECTION_ORDER, isOpen);
    const isTechnician = userRole === 'technician';

    const [formData, setFormData] = useState<CreateJobDTO>({
        date_executed_start: toDateTimeInputValue(new Date()),
        date_executed_end: toDateTimeInputValue(new Date()),
        job_type_id: '',
        address: '',
        unit_name: '',
        assigned_technician_id: '',
        notes: '',
        description: '',
        internal_notes: '',
        invoice_number: '',
        company_id: '',
        estimate: '',
        total: 0,
        status_id: '',
        source_id: '',
        payment_status: 'NotPaid' as PaymentStatus,
        source_commission: undefined,
        hide_contact_phone: false,
        route_calls_to_technician: false,
        is_asap: false,
    });

    // Set company from global filter when modal opens, or apply initialValues pre-population
    useEffect(() => {
        if (!isOpen) return;
        if (initialValues) {
            setFormData(prev => ({
                ...prev,
                address: initialValues.address ?? prev.address,
                unit_name: initialValues.unit_name ?? prev.unit_name,
                company_id: initialValues.company_id ?? globalCompanyId ?? prev.company_id,
                assigned_technician_id: initialValues.assigned_technician_id ?? prev.assigned_technician_id,
                notes: initialValues.notes ?? prev.notes,
                job_type_id: initialValues.job_type_id ?? prev.job_type_id,
                origin_job_id: initialValues.origin_job_id ?? undefined,
            }));
            if (initialValues.contacts && initialValues.contacts.length > 0) {
                setPendingContacts(initialValues.contacts.map(c => ({
                    id: c.id,
                    contact: c,
                    notes: undefined,
                })));
            }
        } else if (globalCompanyId && !formData.company_id) {
            setFormData(prev => ({ ...prev, company_id: globalCompanyId }));
        }
    }, [isOpen]);

    const [pendingContacts, setPendingContacts] = useState<PendingContact[]>([]);
    const [isContactFormOpen, setContactFormOpen] = useState(false);
    const [isContactSearchOpen, setContactSearchOpen] = useState(false);
    const [contactFormMode, setContactFormMode] = useState<'create' | 'edit'>('create');
    const [contactEditing, setContactEditing] = useState<PendingContact | null>(null);
    const [isSavingContact, setIsSavingContact] = useState(false);

    const [startPickerOpen, setStartPickerOpen] = useState(false);
    const [endPickerOpen, setEndPickerOpen] = useState(false);
    const [scheduleExpanded, setScheduleExpanded] = useState(false);

    useBackNavigationLayer({
        enabled: startPickerOpen || endPickerOpen,
        onBack: () => {
            setStartPickerOpen(false);
            setEndPickerOpen(false);
        }
    });

    const layer = useModalLayer({
        enabled: isOpen && !isContactFormOpen && !isContactSearchOpen,
        onClose: () => { resetForm(); onClose(); },
    });

    const [isAdditionalFieldsExpanded, setIsAdditionalFieldsExpanded] = useState(false);
    const [isAddCustomFieldOpen, setIsAddCustomFieldOpen] = useState(false);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [showInactiveTechnicians, setShowInactiveTechnicians] = useState(false);
    const [showSchedulerAssistant, setShowSchedulerAssistant] = useState(false);

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [extraFields, setExtraFields] = useState<Record<string, any>>({});
    const [_, setListInputValues] = useState<Record<string, string>>({});
    const [selectedTags, setSelectedTags] = useState<TagDTO[]>([]);

    // "Fill from message": paste a job message, the backend extracts the fields, the form gets filled.
    const [analysisText, setAnalysisText] = useState('');
    const [isAnalysisExpanded, setIsAnalysisExpanded] = useState(false);
    /** Prefill for the contact form when an analyzed contact needs the user's decision. */
    const [contactPrefill, setContactPrefill] = useState<{ name: string; phone: string; email: string } | null>(null);

    /** Avoid re-applying default schedule times on every render; reset when modal closes. */
    const scheduleDefaultsAppliedKeyRef = useRef<string | null>(null);

    const statusDropdownRef = useRef<HTMLDivElement>(null);

    const queryClient = useQueryClient();
    const addressInputRef = useRef<HTMLInputElement>(null);
    const { addNotification } = useNotifications();

    const handleInputChange = useCallback((field: keyof CreateJobDTO, value: any) => {
        setFormData(prev => {
            const updated = { ...prev, [field]: value };

            // Reset dependent fields when company changes
            if (field === 'company_id') {
                updated.job_type_id = '';
                updated.assigned_technician_id = '';
                updated.status_id = '';
                updated.source_id = '';
            }

            // When start moves past end, align end to start (same as JobDetailsDesktop).
            // Do not adjust start when end changes — picker onChange can fire intermediate
            // values (e.g. "now") while the user is still selecting a date.
            if (field === 'date_executed_start' && value && prev.date_executed_end) {
                const startMs = parseBackendInstantToUtcDate(String(value)).getTime();
                const endMs = parseBackendInstantToUtcDate(prev.date_executed_end).getTime();
                if (!Number.isNaN(startMs) && !Number.isNaN(endMs) && startMs > endMs) {
                    updated.date_executed_end = value;
                }
            }

            return updated;
        });
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    }, [errors]);

    // Get extensible field configurations for jobs - use all fields, not just visible ones
    // Use company-specific fields if a company is selected, otherwise use global fields
    const { fieldConfigs } = useExtensibleFields('jobs', formData.company_id || undefined);
    const jobExtensibleFields = fieldConfigs || [];

    // Memoized handler for Google Maps place selection (matches JobDetailsDesktop pattern)
    const handlePlaceSelect = useCallback((place: google.maps.places.PlaceResult) => {
        const addressWithZip = getAddressWithZipCode(place);
        if (addressWithZip) {
            handleInputChange('address', addressWithZip);
        }
    }, [handleInputChange]);

    // Google Maps autocomplete for address
    const { error: autocompleteError } = useGoogleMapsAutocomplete({
        inputRef: addressInputRef,
        onPlaceSelect: handlePlaceSelect,
        isActive: isOpen,
    });

    // Monitor the address input field for external changes (like Google Maps autocomplete)
    // This effect syncs the input value with React state
    useEffect(() => {
        const inputElement = addressInputRef.current;
        if (!inputElement) return;

        const handleInput = (e: Event) => {
            const target = e.target as HTMLInputElement;
            // Only sync if value differs - handles both manual typing and autocomplete
            if (target.value !== formData.address) {
                handleInputChange('address', target.value);
            }
        };

        inputElement.addEventListener('input', handleInput);

        return () => {
            inputElement.removeEventListener('input', handleInput);
        };
    }, [formData.address, handleInputChange]);

    // Fetch job types, technicians, and companies
    const { data: jobTypes, isLoading: jobTypesLoading } = useQuery({
        queryKey: ['jobTypes', formData.company_id],
        queryFn: () => apiService.getJobTypesByCompany(formData.company_id),
        enabled: isOpen && !!formData.company_id,
    });

    const { data: technicians, isLoading: techniciansLoading } = useQuery({
        queryKey: ['technicians', formData.company_id],
        queryFn: () => apiService.getTechnicians(formData.company_id),
        enabled: isOpen && !!formData.company_id,
    });

    useEffect(() => {
        setShowInactiveTechnicians(false);
    }, [formData.company_id]);

    const { data: companies, isLoading: companiesLoading } = useQuery({
        queryKey: ['companies'],
        queryFn: () => apiService.getCompanies(),
        enabled: isOpen,
    });

    const { data: jobSources, isLoading: jobSourcesLoading } = useQuery({
        queryKey: ['jobSources', formData.company_id],
        queryFn: () => apiService.getJobSourcesByCompany(formData.company_id),
        enabled: isOpen && !!formData.company_id,
    });

    const { data: jobStatuses, isLoading: jobStatusesLoading } = useQuery({
        queryKey: ['jobStatuses', formData.company_id],
        queryFn: () => apiService.getJobStatusesByCompany(formData.company_id),
        enabled: isOpen && !!formData.company_id,
    });

    // Auto-select default status for new jobs when job statuses load
    useEffect(() => {
        if (jobStatuses && jobStatuses.length > 0 && !formData.status_id) {
            // First try to find the status marked as default for new jobs
            const defaultStatus = jobStatuses.find((s: JobStatusDTO) => s.is_default_for_new_job);
            if (defaultStatus) {
                setFormData(prev => ({ ...prev, status_id: defaultStatus.id }));
            } else {
                // Fallback to "Pending" by name for backwards compatibility
                const pendingStatus = jobStatuses.find((s: JobStatusDTO) => s.name.toLowerCase() === 'pending');
                if (pendingStatus) {
                    setFormData(prev => ({ ...prev, status_id: pendingStatus.id }));
                } else {
                    // Final fallback to first status
                    setFormData(prev => ({ ...prev, status_id: jobStatuses[0].id }));
                }
            }
        }
    }, [jobStatuses, formData.status_id]);

    // Helper function to get text color based on background brightness (for status pills)
    const getStatusTextColor = (bgColor?: string) => {
        const backgroundColor = bgColor || '#64748b';
        const hex = backgroundColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return brightness > 128 ? '#0f172a' : '#ffffff';
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(target)) {
                setShowStatusDropdown(false);
            }
        };

        if (showStatusDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showStatusDropdown]);    // Determine the selected company and whether its next invoice id exists.
    // If the company provides a `next_invoice_number` (or common variants), the
    // Invoice ID will be auto-filled and displayed in the header.
    const selectedCompany = companies?.find((c: CompanyDTO) => c.id === formData.company_id);
    const selectedCompanyNextInvoiceRaw = selectedCompany
        ? (selectedCompany.next_invoice_number ?? null)
        : null;

    // Default schedule to "now" in the company timezone when configured; otherwise browser-local "now".
    useEffect(() => {
        if (!isOpen) {
            scheduleDefaultsAppliedKeyRef.current = null;
            return;
        }
        if (!formData.company_id || !companies?.length) return;
        const company = companies.find((c: CompanyDTO) => c.id === formData.company_id);
        if (!company) return;

        const tzKey = company.timezone?.trim() || 'local';
        const applyKey = `${formData.company_id}-${tzKey}`;
        if (scheduleDefaultsAppliedKeyRef.current === applyKey) return;
        scheduleDefaultsAppliedKeyRef.current = applyKey;

        if (company.timezone?.trim()) {
            const now = dayjs().tz(company.timezone.trim());
            setFormData((prev) => ({
                ...prev,
                date_executed_start: now.toISOString(),
                date_executed_end: now.toISOString(),
            }));
        } else {
            const n = new Date();
            setFormData((prev) => ({
                ...prev,
                date_executed_start: toDateTimeInputValue(n),
                date_executed_end: toDateTimeInputValue(n),
            }));
        }
    }, [isOpen, formData.company_id, companies]);

    useEffect(() => {
        if (!formData.company_id) {
            setFormData((prev) => ({ ...prev, invoice_number: '' }));
            return;
        }

        if (!selectedCompany) return;

        if (selectedCompanyNextInvoiceRaw != null) {
            setFormData((prev) => ({ ...prev, invoice_number: String(selectedCompanyNextInvoiceRaw) }));
        } else {
            // Company selected but no next invoice defined -> allow manual entry
            setFormData((prev) => ({ ...prev, invoice_number: '' }));
        }
    }, [formData.company_id, companies]);

    // Create job mutation
    const createJobMutation = useMutation({
        mutationFn: async (data: CreateJobDTO) => {
            const createdJob = await apiService.createJob(data);
            // Add tags after job creation if any were selected
            if (selectedTags.length > 0 && createdJob?.id) {
                const tagInputs = selectedTags.map(tag => ({ tag_id: tag.id }));
                await apiService.addJobTags(createdJob.id, tagInputs);
            }
            return createdJob;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
            onJobCreated();
            resetForm();
            addNotification(notificationService.success('Job created', 'The job was created successfully.'));
        },
        onError: (error: any) => {
            clientLogger.error('Failed to create job:', error);
            setErrors({ submit: 'Failed to create job. Please try again.' });
            addNotification(notificationService.error('Job creation failed', error.message ?? 'Please try again.'));
        },
    });

    // Same timezone the schedule pickers use: the company's, else the browser's.
    const selectedCompanyTz = companies?.find((c: CompanyDTO) => c.id === formData.company_id)?.timezone?.trim() || null;

    const analyzeMutation = useMutation({
        mutationFn: () => apiService.analyzeJobText(
            analysisText.trim(),
            toDateTimeInputValueInTz(new Date(), selectedCompanyTz),
            formData.company_id!,
        ),
        onSuccess: (result) => {
            applyAnalysis(result);
            setIsAnalysisExpanded(false);
            addNotification(notificationService.success('Filled from message', 'Review the fields before creating the job.'));
        },
        onError: (error: any) => {
            addNotification(notificationService.error(
                'Could not fill from message',
                error?.response?.status === 429
                    ? 'Too many requests. Wait a minute and try again.'
                    : apiErrorMessage(error, 'Please fill in the job manually.'),
            ));
        },
    });

    const applyAnalysis = (r: JobTextAnalysisDTO) => {
        // Schedule values come back as wall clocks; convert the same way the pickers store them.
        const toFormDate = (local: string) => (selectedCompanyTz ? dateTimeInputInTimeZoneToIso(local, selectedCompanyTz) : local);

        // One update, not handleInputChange: that path clears job type etc. when company changes.
        setFormData(prev => {
            const next = { ...prev };
            // Keep anything the user already typed; notes are appended instead.
            if (!next.job_type_id && r.job_type_id) next.job_type_id = r.job_type_id;
            if (!next.source_id && r.source_id) next.source_id = r.source_id;
            if (!next.address && r.address) next.address = r.address;
            if (!next.unit_name && r.unit_name) next.unit_name = r.unit_name;
            if (!next.description && r.description) next.description = r.description;
            if (!next.estimate && r.estimate) next.estimate = r.estimate;
            if (r.notes && !next.notes.includes(r.notes)) next.notes = next.notes ? `${next.notes}\n\n${r.notes}` : r.notes;
            // The schedule is never empty (it defaults to "now"), so the message's schedule wins.
            if (r.is_asap) {
                next.is_asap = true;
            } else if (r.start_local) {
                next.is_asap = false;
                next.date_executed_start = toFormDate(r.start_local);
                next.date_executed_end = toFormDate(r.end_local ?? r.start_local);
            }
            return next;
        });

        applyAnalyzedContact(r);
    };

    /** Same rule as ContactFormModal's manual add: a known phone with the same name is attached
     *  silently; a known phone with a different name (or several contacts on it) is the user's call. */
    const applyAnalyzedContact = ({ contact, matching_contacts }: JobTextAnalysisDTO) => {
        if (!contact) return;
        const name = contact.name?.trim() ?? '';
        const phone = contact.phone?.trim() ?? '';
        const email = contact.email?.trim() ?? '';

        const alreadyAdded = pendingContacts.some(p =>
            (phone && (p.contact.phone === phone || p.contact.additional_phone === phone))
            || matching_contacts.some(m => m.id === p.id));
        if (alreadyAdded) return;

        const sameName = name ? matching_contacts.find(m => m.name.trim().toLowerCase() === name.toLowerCase()) : undefined;
        if (sameName) {
            handleContactResolution({ action: 'useExisting', contactId: sameName.id, contact: sameName });
            return;
        }
        if (name && phone && matching_contacts.length === 0) {
            handleContactResolution({ action: 'create', contact: { name, phone, email: email || undefined } });
            return;
        }
        // Different name on a known phone, or name/phone missing: open the contact form prefilled.
        setContactEditing(null);
        setContactFormMode('create');
        setContactPrefill({ name, phone, email });
        setContactFormOpen(true);
    };

    const resetForm = () => {
        setFormData({
            date_executed_start: toDateTimeInputValue(new Date()),
            date_executed_end: toDateTimeInputValue(new Date()),
            job_type_id: '',
            address: '',
            unit_name: '',
            assigned_technician_id: '',
            notes: '',
            description: '',
            internal_notes: '',
            invoice_number: '',
            company_id: '',
            estimate: '',
            total: 0,
            status_id: '',
            source_id: '',
            payment_status: 'NotPaid' as PaymentStatus,
            hide_contact_phone: false,
            route_calls_to_technician: false,
            is_asap: false,
        });
        setPendingContacts([]);
        setContactEditing(null);
        setExtraFields({});
        setListInputValues({});
        setSelectedTags([]);
        setErrors({});
        setScheduleExpanded(false);
        setAnalysisText('');
        setIsAnalysisExpanded(false);
        setContactPrefill(null);
    };

    const generateTempContactId = () =>
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const handleRemovePendingContact = (id: string) => {
        setPendingContacts((prev) => prev.filter((contact) => contact.id !== id));
    };

    const handleContactResolution = (resolution: import('./contacts/ContactFormModal').ContactResolution) => {
        const tempId = generateTempContactId();

        switch (resolution.action) {
            case 'create': {
                const entry: PendingContact = {
                    id: tempId,
                    contact: {
                        id: tempId,
                        name: resolution.contact.name,
                        phone: resolution.contact.phone || '',
                        additional_phone: resolution.contact.additional_phone,
                        email: resolution.contact.email,
                    },
                    createPayload: {
                        name: resolution.contact.name,
                        phone: resolution.contact.phone || '',
                        additional_phone: resolution.contact.additional_phone,
                        email: resolution.contact.email,
                        notes: resolution.contact.notes,
                    },
                    notes: resolution.assignmentNotes
                };
                setPendingContacts(prev => [...prev, entry]);
                break;
            }
            case 'useExisting': {
                if (pendingContacts.some(item => item.id === resolution.contactId)) return;

                const entry: PendingContact = {
                    id: resolution.contactId,
                    contact: resolution.contact ? {
                        id: resolution.contactId,
                        name: resolution.contact.name,
                        phone: resolution.contact.phone || '',
                        email: resolution.contact.email
                    } : {
                        id: resolution.contactId,
                        name: 'Loading...',
                        phone: ''
                    },
                    notes: resolution.assignmentNotes
                };
                setPendingContacts(prev => [...prev, entry]);
                break;
            }
            case 'createNew': {
                const entry: PendingContact = {
                    id: tempId,
                    contact: {
                        id: tempId,
                        name: resolution.contact.name,
                        phone: resolution.contact.phone || '',
                        additional_phone: resolution.contact.additional_phone,
                        email: resolution.contact.email,
                    },
                    createPayload: {
                        name: resolution.contact.name,
                        phone: resolution.contact.phone || '',
                        additional_phone: resolution.contact.additional_phone,
                        email: resolution.contact.email,
                        notes: resolution.contact.notes,
                    },
                    notes: resolution.assignmentNotes
                };
                setPendingContacts(prev => [...prev, entry]);
                break;
            }
        }
        setContactFormOpen(false);
        setContactPrefill(null);
    };

    const handleContactFormSubmit = async ({
        contact,
        assignmentNotes,
    }: {
        contact: ContactCreateDTO | ContactUpdateDTO;
        assignmentNotes?: string;
    }) => {
        setIsSavingContact(true);
        try {
            if (contactFormMode === 'edit' && contactEditing) {
                setPendingContacts((prev) =>
                    prev.map((item) => {
                        if (item.id !== contactEditing.id) {
                            return item;
                        }
                        if (item.createPayload) {
                            return {
                                ...item,
                                contact: {
                                    ...item.contact,
                                    name: contact.name ?? item.contact.name,
                                    phone: contact.phone ?? item.contact.phone,
                                    additional_phone: (contact as ContactUpdateDTO).additional_phone ?? item.contact.additional_phone,
                                    email: contact.email,
                                    notes: contact.notes,
                                },
                                createPayload: {
                                    name: contact.name ?? '',
                                    phone: contact.phone ?? '',
                                    additional_phone: (contact as ContactUpdateDTO).additional_phone,
                                    email: contact.email,
                                    notes: contact.notes,
                                },
                                notes: assignmentNotes,
                            };
                        }
                        return {
                            ...item,
                            contact: {
                                ...item.contact,
                                name: contact.name ?? item.contact.name,
                                phone: contact.phone ?? item.contact.phone,
                                email: contact.email,
                                notes: contact.notes,
                            },
                            notes: assignmentNotes,
                        };
                    })
                );
            }
            // Create flow handled by onResolution
            setContactFormOpen(false);
            setContactEditing(null);
        } catch (error: any) {
            clientLogger.error('Failed to prepare contact', error);
            addNotification(
                notificationService.error('Failed to prepare contact', error.message ?? 'Please try again.')
            );
        } finally {
            setIsSavingContact(false);
        }
    };

    const handleSelectExistingContact = (contact: ContactDTO) => {
        setContactSearchOpen(false);
        setPendingContacts((prev) => {
            if (prev.some((item) => item.id === contact.id)) {
                return prev;
            }
            const entry: PendingContact = {
                id: contact.id,
                contact,
                notes: undefined,
            };
            return [...prev, entry];
        });
    };

    const openNewContactModal = () => {
        setContactFormMode('create');
        setContactEditing(null);
        setContactFormOpen(true);
    };

    const openEditPendingContact = (pending: PendingContact) => {
        setContactFormMode('edit');
        setContactEditing(pending);
        setContactFormOpen(true);
    };

    const handleExtraFieldChange = (fieldName: string, value: any) => {
        setExtraFields(prev => ({ ...prev, [fieldName]: value }));
        // Clear error when user starts typing
        if (errors[`extra_${fieldName}`]) {
            setErrors(prev => ({ ...prev, [`extra_${fieldName}`]: '' }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.company_id) {
            newErrors.company_id = 'Company is required';
        }

        if (!formData.is_asap && formData.date_executed_start && formData.date_executed_end) {
            const startMs = parseBackendInstantToUtcDate(formData.date_executed_start).getTime();
            const endMs = parseBackendInstantToUtcDate(formData.date_executed_end).getTime();
            if (!Number.isNaN(startMs) && !Number.isNaN(endMs) && endMs < startMs) {
                newErrors.date_executed_end = 'End date cannot be before start date';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Helper function to convert empty strings to null recursively
    // Excludes "notes" field which should remain as empty string
    const convertEmptyStringsToNull = (obj: any, parentKey?: string): any => {
        if (obj === null || obj === undefined) {
            return obj;
        }

        if (typeof obj === 'string') {
            // Keep empty string for "notes" field, convert others to null
            if (parentKey === 'notes') {
                return obj;
            }
            return obj === '' ? null : obj;
        }

        if (Array.isArray(obj)) {
            return obj.map(item => convertEmptyStringsToNull(item, parentKey));
        }

        if (typeof obj === 'object') {
            const converted: any = {};
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    converted[key] = convertEmptyStringsToNull(obj[key], key);
                }
            }
            return converted;
        }

        return obj;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const hasExtraFields = Object.keys(extraFields).some((key) => {
            const value = extraFields[key];
            return value !== '' && value !== null && value !== undefined;
        });

        // Transform pendingContacts into JobContactInputDTO format
        const contacts: JobContactInputDTO[] = pendingContacts.map((pending) => {
            if (pending.createPayload) {
                // New contact: set id to null and include contact data
                return {
                    id: undefined, // API expects null for new contacts
                    contact: pending.createPayload,
                    notes: pending.notes,
                };
            } else {
                // Existing contact: include the id
                return {
                    id: pending.id,
                    notes: pending.notes,
                };
            }
        });

        // ASAP: the date is a placeholder for when the job came in, stamped at submit time.
        const now = new Date().toISOString();
        const payload = convertEmptyStringsToNull({
            ...formData,
            ...(formData.is_asap ? { date_executed_start: now, date_executed_end: now } : {}),
            total: null, // Total is always null on job creation
            contacts: contacts.length > 0 ? contacts : undefined,
            extra_fields: hasExtraFields ? convertEmptyStringsToNull(extraFields) : undefined,
        }) as CreateJobDTO;

        createJobMutation.mutate(payload);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
            e.preventDefault();
        }
    };

    const contactItems: ContactListItem[] = pendingContacts.map((pending) => ({
        id: pending.id,
        contact: pending.contact,
        notes: pending.notes,
    }));

    const handleClose = () => {
        layer.onClose();
    };

    const startPickerValue = usePickerValue(formData.date_executed_start);
    const endPickerValue = usePickerValue(formData.date_executed_end);

    if (!isOpen) return null;

    // Lays out the form boxes: saved order + drag handles on desktop, default order on phones.
    // Hidden boxes (no company yet, technician role, no extra fields) are left out.
    const selectedJobStatus = jobStatuses?.find((st: JobStatusDTO) => st.id === formData.status_id);

    // "Msg Auto Fill" (BETA): opens a small window to paste the job message.
    const fillFromMessageButton = (
        <button
            type="button"
            onClick={() => setIsAnalysisExpanded(true)}
            className="inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-lg border border-violet-200 bg-violet-50 px-2 text-xs font-semibold text-violet-700 hover:bg-violet-100 transition-colors"
            title="Fill the form from a pasted message"
        >
            <Sparkles className="h-3.5 w-3.5" />
            Msg Auto Fill
        </button>
    );

    const fillFromMessageDialog = isAnalysisExpanded && (
        <ItemEditorDialog
            title="Fill from message (BETA)"
            icon={Sparkles}
            onClose={() => setIsAnalysisExpanded(false)}
            footer={
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => setIsAnalysisExpanded(false)}
                        className="flex-1 sm:flex-none sm:min-w-[120px] px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="flex-1 sm:flex-none sm:min-w-[120px] px-4 py-2.5 text-sm font-semibold text-white bg-primary-600 rounded-xl shadow-sm hover:bg-primary-700 transition-colors disabled:opacity-50"
                        disabled={!formData.company_id || jobTypesLoading || jobSourcesLoading || !analysisText.trim() || analyzeMutation.isPending}
                        onClick={() => analyzeMutation.mutate()}
                    >
                        {analyzeMutation.isPending ? 'Filling…' : 'Fill form'}
                    </button>
                </div>
            }
        >
            <div className="space-y-2">
                <textarea
                    value={analysisText}
                    onChange={(e) => setAnalysisText(e.target.value)}
                    maxLength={4000}
                    rows={7}
                    aria-label="Job message to fill the form from"
                    placeholder="Paste the job message (SMS, WhatsApp, email). The form gets filled for you to review."
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none resize-y"
                />
                <p className="text-xs text-gray-500">
                    {formData.company_id ? 'Fields you already filled are kept.' : 'Select a company first.'}
                </p>
            </div>
        </ItemEditorDialog>
    );

    // ASAP checkbox: sits in front of "Set end time" (or alone while ASAP is on).
    const asapToggle = (
        <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
                type="checkbox"
                checked={formData.is_asap ?? false}
                onChange={(e) => handleInputChange('is_asap', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
            />
            <span className="text-xs font-medium text-gray-700">ASAP</span>
        </label>
    );

    const renderFormSections = (sections: Record<NewJobSectionId, ReactNode>) => (
        <ReorderableSections
            sections={sections}
            order={sectionOrder.order}
            hidden={{
                tags: !formData.company_id,
                internalNotes: isTechnician,
                additionalFields: jobExtensibleFields.length === 0,
            }}
            labels={NEW_JOB_SECTION_LABELS}
            isReordering={sectionOrder.isReordering}
            isCustom={sectionOrder.isCustom}
            isMobile={isMobile}
            onReorder={sectionOrder.reorder}
            onReset={sectionOrder.reset}
            onDone={() => sectionOrder.setIsReordering(false)}
        />
    );

    const paddingClass = isMobile ? 'p-3' : 'p-3';
    const gapClass = isMobile ? 'gap-3' : 'gap-3';
    const modalMaxWidth = isMobile ? 'max-w-full' : 'max-w-7xl';
    const scrollLockedFooter = isMobile && !isVirtualKeyboardOpen;

    return createPortal(
        <LayerDepthContext.Provider value={layer.depth}>
            <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4" style={{ ...layer.style, ...splitBounds }}>
                <div className={`bg-gray-50 sm:rounded-xl shadow-xl w-full ${modalMaxWidth} h-full ${splitBounds ? 'sm:max-h-full' : 'sm:max-h-[90vh] safe-area-top'} overflow-hidden flex flex-col`}>
                    <div className={`bg-white flex items-center justify-between ${paddingClass} border-b shrink-0`}>
                        <div className="flex-1 flex flex-col lg:flex-row lg:items-center lg:gap-6">
                            <div className="flex items-center gap-4">
                                <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-gray-900 flex-shrink-0`}>New Job #{formData.invoice_number}</h2>
                                {/* Company Selection in Header */}
                                {companiesLoading ? (
                                    <LoadingSpinner />
                                ) : (
                                    <div className="flex flex-col relative">
                                        <CustomSelect
                                            value={formData.company_id || ''}
                                            onChange={(value) => handleInputChange('company_id', value || undefined)}
                                            options={[
                                                { value: '', label: 'Select a company' },
                                                ...(companies?.map((company: CompanyBasicDTO) => ({
                                                    value: company.id,
                                                    label: company.name
                                                })) || [])
                                            ]}
                                            placeholder="Select a company"
                                            className={`max-w-[200px] ${errors.company_id ? 'border-red-500' : ''}`}
                                        />
                                        {errors.company_id && (
                                            <p className="text-red-500 text-[10px] mt-0.5 absolute top-full">{errors.company_id}</p>
                                        )}
                                    </div>
                                )}
                            </div>
                            {/* Status bar: Job Status, Reorder */}
                            <div className="flex items-center gap-2 mt-2 lg:mt-0 flex-wrap">
                                {/* Job Status - Custom Dropdown */}
                                <div className="relative" ref={statusDropdownRef}>
                                    <button
                                        type="button"
                                        onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                                        disabled={!formData.company_id || jobStatusesLoading}
                                        className={`${STATUS_BADGE_CLASS} transition-all hover:brightness-95 disabled:opacity-50`}
                                        style={statusBadgeStyle(selectedJobStatus?.color, !!selectedJobStatus?.is_default_for_completed_job)}
                                    >
                                        <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: statusDotColor(selectedJobStatus?.color, !!selectedJobStatus?.is_default_for_completed_job) }} />
                                        <span>
                                            {!formData.company_id
                                                ? 'Select company'
                                                : jobStatusesLoading
                                                    ? 'Loading...'
                                                    : jobStatuses?.find((s: JobStatusDTO) => s.id === formData.status_id)?.name || 'Select Status'
                                            }
                                        </span>
                                        <ChevronDown className="h-3 w-3 opacity-70" />
                                    </button>

                                    {/* Status Dropdown Menu */}
                                    {showStatusDropdown && jobStatuses && (
                                        <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-48 overflow-y-auto animate-in fade-in slide-in-from-top-2 flex flex-col">
                                            {jobStatuses.map((status: JobStatusDTO) => {
                                                const statusBgColor = status.color || '#808080';
                                                const textColor = getStatusTextColor(statusBgColor);
                                                const isSelected = formData.status_id === status.id;

                                                return (
                                                    <button
                                                        key={status.id}
                                                        type="button"
                                                        onClick={() => {
                                                            handleInputChange('status_id', status.id);
                                                            setShowStatusDropdown(false);
                                                        }}
                                                        className={`block w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                                                    >
                                                        <div
                                                            className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold"
                                                            style={{ backgroundColor: statusBgColor, color: textColor }}
                                                        >
                                                            {status.name}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Reorder boxes (desktop; phones have it under the ✕) */}
                                {!isMobile && (
                                    <ReorderButton
                                        isReordering={sectionOrder.isReordering}
                                        onToggle={() => sectionOrder.setIsReordering((on) => !on)}
                                    />
                                )}
                                {/* Desktop: after Reorder. Phones: here on the left, leaving room for Reorder under the ✕. */}
                                {fillFromMessageButton}
                            </div>
                        </div>
                        <div className="flex flex-col items-end self-start">
                            <button
                                onClick={handleClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-2 -mr-2"
                                style={{ minWidth: '44px', minHeight: '44px' }}
                            >
                                <X className="h-6 w-6" />
                            </button>
                            {isMobile && (
                                <ReorderButton
                                    isReordering={sectionOrder.isReordering}
                                    onToggle={() => sectionOrder.setIsReordering((on) => !on)}
                                />
                            )}
                        </div>
                    </div>

                    <NewJobModalScrollLayout
                        mobileStickyFooter={
                            scrollLockedFooter ? (
                                <div className="shrink-0 bg-white border-t border-gray-200 px-3 pt-3 pb-[max(0.75rem,var(--safe-area-inset-bottom),var(--android-safe-area-bottom))]">
                                    {errors.submit && (
                                        <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3">
                                            <p className="text-red-600 text-sm">{errors.submit}</p>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            className="btn-secondary flex-1"
                                            disabled={createJobMutation.isPending}
                                            style={{ minHeight: '44px' }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            form="new-job-form"
                                            className="btn-primary flex-1"
                                            disabled={createJobMutation.isPending}
                                            style={{ minHeight: '44px' }}
                                        >
                                            {createJobMutation.isPending ? (
                                                <>
                                                    <LoadingSpinner />
                                                    Creating...
                                                </>
                                            ) : (
                                                'Create Job'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ) : null
                        }
                    >
                        <form id="new-job-form" onSubmit={handleSubmit} onKeyDown={handleKeyDown} className={paddingClass}>
                            <div className={`grid ${isNarrowSplit ? 'grid-cols-2' : `grid-cols-1 ${!isMobile ? 'lg:grid-cols-[1fr_400px]' : ''}`} ${gapClass}`}>
                                {/* Main Form Content */}
                                <div className="space-y-1.5">
                                    {/* Boxes in the user's saved order (drag the grip on desktop to move a box) */}
                                    {renderFormSections({
                                        location: (
                                            <>
                                    {/* 1. LOCATION CARD - First priority */}
                                    <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2">
                                        <div className="space-y-1">
                                            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                                <MapPin className="h-3.5 w-3.5 text-rose-500" />
                                                Location
                                            </label>
                                            {/* Hidden dummy inputs to confuse browser autocomplete */}
                                            <input type="hidden" autoComplete="off" tabIndex={-1} name="fake-address-line1" />
                                            <input type="hidden" autoComplete="off" tabIndex={-1} name="fake-address-line2" />
                                            <input
                                                ref={addressInputRef}
                                                type="text"
                                                value={formData.address}
                                                onChange={(e) => handleInputChange('address', e.target.value)}
                                                className={`w-full text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none ${errors.address ? 'border-red-500' : ''}`}
                                                placeholder="Enter address"
                                                autoComplete="off"
                                                autoCorrect="off"
                                                autoCapitalize="off"
                                                spellCheck="false"
                                                data-form-type="other"
                                                data-lpignore="true"
                                                name="job-location-description"
                                                role="textbox"
                                                aria-label="Job address"
                                            />
                                            <input
                                                type="text"
                                                value={formData.unit_name || ''}
                                                onChange={(e) => handleInputChange('unit_name', e.target.value)}
                                                className="w-full text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none mt-1.5"
                                                placeholder="Unit/Business Name # (optional)"
                                                autoComplete="off"
                                            />
                                            {errors.address && (
                                                <p className="text-red-500 text-xs">{errors.address}</p>
                                            )}
                                            {autocompleteError && (
                                                <p className="text-amber-600 text-xs flex items-center gap-1">
                                                    ⚠️ Autocomplete unavailable. You can still type manually.
                                                </p>
                                            )}
                                        </div>

                                        {/* Map Widget - Show below address on mobile */}
                                        {isMobile && (
                                            <MapWidget address={formData.address} height="150px" />
                                        )}
                                    </div>
                                            </>
                                        ),
                                        contacts: (
                                            <>
                                    {/* 2. CONTACTS CARD - Second priority */}
                                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                                <User className="h-3.5 w-3.5 text-indigo-500" />
                                                Contacts
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={openNewContactModal}
                                                    className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-0.5"
                                                >
                                                    <Plus className="h-3 w-3" />
                                                    New
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setContactSearchOpen(true)}
                                                    className="text-xs text-primary-600 hover:text-gray-700 font-medium flex items-center gap-0.5"
                                                >
                                                    <User className="h-3 w-3" />
                                                    Existing
                                                </button>
                                            </div>
                                        </div>
                                        {errors.contacts && (
                                            <p className="mb-1.5 text-xs text-red-500">{errors.contacts}</p>
                                        )}
                                        {contactItems.length === 0 ? (
                                            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-2 text-center">
                                                <p className="text-xs text-gray-500">No contacts added</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-1.5">
                                                {contactItems.map((item) => (
                                                    <div
                                                        key={item.id}
                                                        className="flex items-center justify-between px-2 py-1.5 rounded-lg text-sm bg-gray-50 border border-gray-200"
                                                    >
                                                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                                            <User className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                                                            <span className="font-medium truncate text-gray-900 text-sm">{item.contact.name}</span>
                                                        </div>
                                                        <div className="flex items-center flex-shrink-0">
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const pending = pendingContacts.find((contact) => contact.id === item.id);
                                                                    if (pending) {
                                                                        openEditPendingContact(pending);
                                                                    }
                                                                }}
                                                                className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                                                                title="Edit"
                                                            >
                                                                <FileText className="h-3.5 w-3.5" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemovePendingContact(item.id)}
                                                                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                                                title="Remove"
                                                            >
                                                                <X className="h-3.5 w-3.5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                                                            {/* 9. CALL MASKING - admin only */}
                                    {/* Plan feature too: without it the server clears the flags on save. */}
                                    {isAdmin && isFeatureAllowed('voip.call-masking') && (
                                        <div className="bg-white  border-gray-200 p-3">
                                            <CallMaskingOptions
                                                variant="job"
                                                idPrefix="new_job_masking"
                                                withEnableAll
                                                route={formData.route_calls_to_technician ?? false}
                                                hide={formData.hide_contact_phone ?? false}
                                                onChange={(field, value) => handleInputChange(
                                                    field === 'route' ? 'route_calls_to_technician' : 'hide_contact_phone', value)}
                                                labelClassName="text-xs font-medium text-gray-700"
                                            />
                                        </div>
                                    )}
                                    </div>
                                            </>
                                        ),
                                        date: (
                                            <>
                                    {/* 3. DATE CARD - Third priority */}
                                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                                    <Calendar className="h-3.5 w-3.5 text-amber-500" />
                                                    Schedule
                                                </label>
                                                {isMobile && formData.company_id && !formData.is_asap && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowSchedulerAssistant(prev => !prev)}
                                                        className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                                                    >
                                                        <Calendar className="h-3 w-3" />
                                                        Schedule assist
                                                        {showSchedulerAssistant ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                                    </button>
                                                )}
                                            </div>
                                            {formData.is_asap && asapToggle}
                                            {!formData.is_asap && (() => {
                                                const showRange = scheduleExpanded || isScheduleRange(formData.date_executed_start, formData.date_executed_end);
                                                const tzProps = selectedCompany?.timezone?.trim()
                                                    ? { timezone: selectedCompany.timezone.trim() }
                                                    : {};
                                                if (!showRange) {
                                                    // Collapsed: one date+time that sets both start and end.
                                                    return (
                                                        <>
                                                            <div className="space-y-1">
                                                                <label className="text-xs font-medium text-gray-600">Date & Time</label>
                                                                <DateTimePicker
                                                                    open={startPickerOpen}
                                                                    onOpen={() => setStartPickerOpen(true)}
                                                                    onClose={() => setStartPickerOpen(false)}
                                                                    sx={gentleDateTimePickerRootSx}
                                                                    {...tzProps}
                                                                    value={startPickerValue}
                                                                    onChange={(newValue) => {
                                                                        const iso = newValue ? newValue.toISOString() : null;
                                                                        setFormData(prev => ({ ...prev, date_executed_start: iso, date_executed_end: iso }));
                                                                    }}
                                                                    viewRenderers={{
                                                                        hours: renderTimeViewClock,
                                                                        minutes: renderTimeViewClock,
                                                                        seconds: renderTimeViewClock,
                                                                    }}
                                                                    slotProps={gentleDateTimePickerSlotProps}
                                                                />
                                                            </div>
                                                            <div className="flex items-center gap-4">
                                                                {asapToggle}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setScheduleExpanded(true)}
                                                                    className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
                                                                >
                                                                    <Plus className="h-3 w-3" />
                                                                    Set end time
                                                                </button>
                                                            </div>
                                                        </>
                                                    );
                                                }
                                                // Expanded: separate Start / End pickers.
                                                return (
                                                    <>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                            <div className="space-y-1">
                                                                <label className="text-xs font-medium text-gray-600">Start Date & Time</label>
                                                                <DateTimePicker
                                                                    open={startPickerOpen}
                                                                    onOpen={() => setStartPickerOpen(true)}
                                                                    onClose={() => setStartPickerOpen(false)}
                                                                    sx={gentleDateTimePickerRootSx}
                                                                    {...tzProps}
                                                                    value={startPickerValue}
                                                                    onChange={(newValue) => handleInputChange('date_executed_start', newValue ? newValue.toISOString() : null)}
                                                                    viewRenderers={{
                                                                        hours: renderTimeViewClock,
                                                                        minutes: renderTimeViewClock,
                                                                        seconds: renderTimeViewClock,
                                                                    }}
                                                                    slotProps={gentleDateTimePickerSlotProps}
                                                                />
                                                            </div>
                                                            <div className="space-y-1">
                                                                <label className="text-xs font-medium text-gray-600">End Date & Time</label>
                                                                <DateTimePicker
                                                                    open={endPickerOpen}
                                                                    onOpen={() => setEndPickerOpen(true)}
                                                                    onClose={() => setEndPickerOpen(false)}
                                                                    sx={gentleDateTimePickerRootSx}
                                                                    {...tzProps}
                                                                    value={endPickerValue}
                                                                    onChange={(newValue) => handleInputChange('date_executed_end', newValue ? newValue.toISOString() : null)}
                                                                    viewRenderers={{
                                                                        hours: renderTimeViewClock,
                                                                        minutes: renderTimeViewClock,
                                                                        seconds: renderTimeViewClock,
                                                                    }}
                                                                    slotProps={gentleDateTimePickerSlotProps}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-4">
                                                            {asapToggle}
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setScheduleExpanded(false);
                                                                    setFormData(prev => ({ ...prev, date_executed_end: prev.date_executed_start }));
                                                                }}
                                                                className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
                                                            >
                                                                <ChevronUp className="h-3 w-3" />
                                                                Use a single date & time
                                                            </button>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                            {errors.date_executed_end && (
                                                <p className="text-red-500 text-xs">{errors.date_executed_end}</p>
                                            )}
                                            {isMobile && showSchedulerAssistant && formData.company_id && !formData.is_asap && (
                                                <SchedulerAssistant
                                                    companyId={formData.company_id}
                                                    formStartDate={formData.date_executed_start}
                                                />
                                            )}
                                        </div>
                                    </div>
                                            </>
                                        ),
                                        jobTypeSource: (
                                            <>
                                    {/* 4. JOB TYPE & SOURCE CARD - Fourth priority */}
                                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="space-y-1">
                                                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                                    <FileText className="h-3.5 w-3.5 text-cyan-500" />
                                                    Job Type
                                                </label>
                                                <CustomSelect
                                                    value={formData.job_type_id}
                                                    onChange={(value) => handleInputChange('job_type_id', value)}
                                                    options={[
                                                        { value: '', label: !formData.company_id ? 'Select company' : jobTypesLoading ? 'Loading...' : 'Select type' },
                                                        ...(jobTypes?.map((jobType: JobTypeBasicDTO) => ({
                                                            value: jobType.id,
                                                            label: jobType.name
                                                        })) || [])
                                                    ]}
                                                    placeholder="Select type"
                                                    selectOnType
                                                    disabled={!formData.company_id || jobTypesLoading}
                                                    className={errors.job_type_id ? 'border-red-500' : ''}
                                                />
                                                {errors.job_type_id && (
                                                    <p className="text-red-500 text-xs">{errors.job_type_id}</p>
                                                )}
                                            </div>

                                            <div className="space-y-1">
                                                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                                    <ArrowRight className="h-3.5 w-3.5 text-violet-500" />
                                                    Source
                                                </label>
                                                <CustomSelect
                                                    value={formData.source_id || ''}
                                                    onChange={(value) => handleInputChange('source_id', value || undefined)}
                                                    options={[
                                                        { value: '', label: !formData.company_id ? 'Select company' : jobSourcesLoading ? 'Loading...' : 'None' },
                                                        ...(jobSources?.map((source: JobSourceDTO) => ({
                                                            value: source.id,
                                                            label: source.name
                                                        })) || [])
                                                    ]}
                                                    placeholder="None"
                                                    selectOnType
                                                    disabled={!formData.company_id || jobSourcesLoading}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                            </>
                                        ),
                                        description: (
                                            <>
                                    {/* DESCRIPTION CARD */}
                                    <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-1">
                                        <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                            <FileText className="h-3.5 w-3.5 text-blue-500" />
                                            Description
                                        </label>
                                        <textarea
                                            value={formData.description || ''}
                                            onChange={(e) => {
                                                handleInputChange('description', e.target.value);
                                                e.target.style.height = 'auto';
                                                e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                                            }}
                                            className="w-full text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none resize-none"
                                            rows={2}
                                            placeholder="Describe the job..."
                                        />
                                    </div>
                                            </>
                                        ),
                                        tags: (
                                            <>
                                    {/* TAGS CARD - After Job Type & Source */}
                                    {formData.company_id && (
                                        <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-1">
                                            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                                Tags
                                            </label>
                                            <TagSelector
                                                companyId={formData.company_id}
                                                selectedTags={selectedTags}
                                                onChange={setSelectedTags}
                                                allowCreate={true}
                                            />
                                        </div>
                                    )}
                                            </>
                                        ),
                                        estimateTechnician: (
                                            <>
                                    {/* 5. ESTIMATE & TECHNICIAN CARD - Fifth priority */}
                                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                                        <div className="grid grid-cols-2 gap-2">
                                            {/* Estimate */}
                                            <div className="space-y-1">
                                                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                                    <DollarSign className="h-3.5 w-3.5 text-emerald-500" />
                                                    Estimate
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={formData.estimate}
                                                        onChange={(e) => handleInputChange('estimate', e.target.value)}
                                                        className={`w-full text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none ${errors.estimate ? 'border-red-500' : ''}`}
                                                        placeholder="Enter estimate"
                                                    />
                                                </div>
                                            </div>

                                            {/* Technician */}
                                            <div className="space-y-1">
                                                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                                    <User className="h-3.5 w-3.5 text-sky-500" />
                                                    Technician
                                                </label>
                                                <CustomSelect
                                                    value={formData.assigned_technician_id || ''}
                                                    onChange={(value) => {
                                                        handleInputChange('assigned_technician_id', value || undefined);
                                                        // Pre-populate call masking from the technician's defaults
                                                        const selectedTech = technicians?.find((t: any) => t.id === value);
                                                        handleInputChange('hide_contact_phone', selectedTech?.default_hide_contact_phone ?? false);
                                                        handleInputChange('route_calls_to_technician', selectedTech?.default_route_calls_to_technician ?? false);
                                                    }}
                                                    options={buildTechnicianSelectOptions(
                                                        technicians,
                                                        showInactiveTechnicians,
                                                        formData.assigned_technician_id || '',
                                                        {
                                                            value: '',
                                                            label: !formData.company_id ? 'Select company' : techniciansLoading ? 'Loading...' : 'Unassigned',
                                                        },
                                                    )}
                                                    placeholder="Unassigned"
                                                    selectOnType
                                                    disabled={!formData.company_id || techniciansLoading}
                                                    footerAction={hasInactiveTechnicians(technicians) && !showInactiveTechnicians
                                                        ? {
                                                            label: 'Show inactive technicians...',
                                                            onClick: () => setShowInactiveTechnicians(true),
                                                        }
                                                        : undefined}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                            </>
                                        ),
                                        notes: (
                                            <>
                                    {/* 6. NOTES CARD - Sixth priority */}
                                    <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-1">
                                        <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                            <FileText className="h-3.5 w-3.5 text-orange-500" />
                                            Notes
                                        </label>
                                        <textarea
                                            value={formData.notes}
                                            onChange={(e) => {
                                                handleInputChange('notes', e.target.value);
                                                // Auto-expand textarea
                                                e.target.style.height = 'auto';
                                                e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                                            }}
                                            className="w-full text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none resize-none"
                                            rows={2}
                                            placeholder="Add notes..."
                                        />
                                    </div>
                                            </>
                                        ),
                                        internalNotes: (
                                            <>
                                    {/* 8. INTERNAL NOTES CARD - hidden for technicians */}
                                    {!isTechnician && (
                                        <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-1">
                                            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                                <FileText className="h-3.5 w-3.5 text-amber-500" />
                                                Internal Notes
                                            </label>
                                            <textarea
                                                value={formData.internal_notes || ''}
                                                onChange={(e) => {
                                                    handleInputChange('internal_notes', e.target.value);
                                                    e.target.style.height = 'auto';
                                                    e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                                                }}
                                                className="w-full text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none resize-none"
                                                rows={2}
                                                placeholder="Add internal notes (admin only)..."
                                            />
                                        </div>
                                    )}
                                            </>
                                        ),
                                        additionalFields: (
                                            <>
                                    {/* 7. ADDITIONAL FIELDS - Last priority (collapsible) */}
                                    {jobExtensibleFields.length > 0 && (
                                        <div className="bg-white rounded-lg border border-gray-200 p-3">
                                            <button
                                                type="button"
                                                onClick={() => setIsAdditionalFieldsExpanded(!isAdditionalFieldsExpanded)}
                                                className="flex items-center justify-between w-full text-left"
                                            >
                                                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                                                    <Settings className="h-3.5 w-3.5 text-slate-500" />
                                                    Additional Fields
                                                    <span className="text-gray-400">({jobExtensibleFields.length})</span>
                                                </label>
                                                {isAdditionalFieldsExpanded ? (
                                                    <ChevronUp className="h-4 w-4 text-gray-400" />
                                                ) : (
                                                    <ChevronDown className="h-4 w-4 text-gray-400" />
                                                )}
                                            </button>
                                            {isAdditionalFieldsExpanded && (
                                                <div className="mt-3 space-y-2">
                                                    {jobExtensibleFields.map((field: ExtraFieldConfigDTO) => (
                                                        <div key={field.name} className="space-y-1">
                                                            <label className="text-xs font-medium text-gray-600">
                                                                {field.name}
                                                            </label>
                                                            {field.type === 'Text' && (
                                                                <input
                                                                    type="text"
                                                                    value={extraFields[field.name] || ''}
                                                                    onChange={(e) => handleExtraFieldChange(field.name, e.target.value)}
                                                                    className={`w-full text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 focus:outline-none ${errors[`extra_${field.name}`] ? 'border-red-500' : ''}`}
                                                                    placeholder={`Enter ${field.name.toLowerCase()}`}
                                                                />
                                                            )}
                                                            {field.type === 'Boolean' && (
                                                                <CustomSelect
                                                                    value={extraFields[field.name] === true ? 'true' : extraFields[field.name] === false ? 'false' : ''}
                                                                    onChange={(value) => handleExtraFieldChange(field.name, value === 'true')}
                                                                    options={[
                                                                        { value: '', label: 'Select an option' },
                                                                        { value: 'true', label: 'Yes' },
                                                                        { value: 'false', label: 'No' }
                                                                    ]}
                                                                    placeholder="Select an option"
                                                                    className={errors[`extra_${field.name}`] ? 'border-red-500' : ''}
                                                                />
                                                            )}
                                                            {errors[`extra_${field.name}`] && (
                                                                <p className="text-red-500 text-xs">{errors[`extra_${field.name}`]}</p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                            </>
                                        ),
                                    })}
                                    {/* Mobile Actions — in scroll flow while keyboard is open */}
                                    {isMobile && isVirtualKeyboardOpen && (
                                        <div className="mt-4 pt-4 border-t border-gray-200 safe-area-bottom-padded">
                                            {errors.submit && (
                                                <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3">
                                                    <p className="text-red-600 text-sm">{errors.submit}</p>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-3">
                                                <button
                                                    type="button"
                                                    onClick={handleClose}
                                                    className="btn-secondary flex-1"
                                                    disabled={createJobMutation.isPending}
                                                    style={{ minHeight: '44px' }}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="btn-primary flex-1"
                                                    disabled={createJobMutation.isPending}
                                                    style={{ minHeight: '44px' }}
                                                >
                                                    {createJobMutation.isPending ? (
                                                        <>
                                                            <LoadingSpinner />
                                                            Creating...
                                                        </>
                                                    ) : (
                                                        'Create Job'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Map Sidebar - Desktop only */}
                                {!isMobile && (
                                    <div className="lg:sticky lg:top-0 h-[calc(100dvh-140px)] sm:h-[calc(90dvh-100px)] flex flex-col pt-3 pb-3">
                                        <div className="flex flex-col flex-1 min-h-0">
                                            {formData.address ? (
                                                <MapWidget address={formData.address} />
                                            ) : (
                                                <div className="bg-gray-50 rounded-lg border border-gray-200 p-8 text-center shrink-0" style={{ height: '300px' }}>
                                                    <MapPin className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                                                    <p className="text-sm text-gray-400">Enter an address to see map</p>
                                                </div>
                                            )}

                                            {/* Scheduler Assistant */}
                                            {formData.company_id && (
                                                <SchedulerAssistant
                                                    companyId={formData.company_id}
                                                    formStartDate={formData.date_executed_start}
                                                />
                                            )}
                                        </div>

                                        {/* Bottom: Form Actions */}
                                        <div className="space-y-3 pt-4 mt-auto shrink-0">
                                            {errors.submit && (
                                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                                    <p className="text-red-600 text-sm">{errors.submit}</p>
                                                </div>
                                            )}
                                            <div className="flex items-center justify-end gap-3">
                                                {/* Admins can add a custom job field without leaving the form */}
                                                {isAdmin && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsAddCustomFieldOpen(true)}
                                                        disabled={!formData.company_id}
                                                        className="mr-auto flex h-11 w-11 items-center justify-center rounded-lg border border-gray-200 bg-white text-slate-500 hover:bg-gray-50 hover:text-primary-600 transition-colors disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-slate-500"
                                                        title={formData.company_id ? 'Add custom field' : 'Select a company first'}
                                                        aria-label="Add custom field"
                                                    >
                                                        <ListPlus className="h-5 w-5" />
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={handleClose}
                                                    className={`btn-secondary ${isNarrowSplit ? 'px-4' : 'px-8'}`}
                                                    disabled={createJobMutation.isPending}
                                                    style={{ minHeight: '44px' }}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    className={`btn-primary ${isNarrowSplit ? 'px-4' : 'px-8'}`}
                                                    disabled={createJobMutation.isPending}
                                                    style={{ minHeight: '44px' }}
                                                >
                                                    {createJobMutation.isPending ? (
                                                        <>
                                                            <LoadingSpinner />
                                                            Creating...
                                                        </>
                                                    ) : (
                                                        'Create Job'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </form>
                    </NewJobModalScrollLayout>
                </div>
            </div>

            {isAdmin && !isMobile && formData.company_id && (
                <AddCustomFieldModal
                    isOpen={isAddCustomFieldOpen}
                    onClose={() => setIsAddCustomFieldOpen(false)}
                    tableName="jobs"
                    companyId={formData.company_id}
                    onAdded={() => setIsAdditionalFieldsExpanded(true)}
                />
            )}

            <ContactFormModal
                isOpen={isContactFormOpen}
                onClose={() => {
                    setContactFormOpen(false);
                    setContactEditing(null);
                    setContactPrefill(null);
                }}
                onSubmit={handleContactFormSubmit}
                onResolution={handleContactResolution}
                contact={contactEditing?.contact}
                companyId={formData.company_id || undefined}
                title={contactFormMode === 'edit' ? 'Edit Contact' : 'New Contact'}
                confirmLabel={contactFormMode === 'edit' ? 'Save Changes' : 'Add Contact'}
                isSubmitting={isSavingContact}
                includeAssignmentNotes
                initialAssignmentNotes={contactEditing?.notes || ''}
                contactFieldsDisabled={contactEditing ? !contactEditing.createPayload : false}
                initialValues={contactPrefill ?? undefined}
            />

            <ContactSearchModal
                isOpen={isContactSearchOpen}
                onClose={() => setContactSearchOpen(false)}
                onSelect={handleSelectExistingContact}
            />

            {fillFromMessageDialog}
        </LayerDepthContext.Provider>,
        document.body
    );
}
